import React, { ReactNode, useContext, memo } from 'react';
import { useIsomorphicLayoutEffect } from '@direct-frontend/stdlib';

import { getClassNames } from '../../lib/css/className/className';

import { addRootTheme } from './addRootTheme';
import type { Theme } from './types';

type ThemeContextProps = Theme;

interface ThemeProviderProps {
    /**
     * Текущая тема приложения
     */
    theme: Theme;

    /**
     * DOM-элемент для CSS-классов темы (dc-Theme*).
     * undefined → document.body, null → не применять
     */
    rootElement?: HTMLElement | null;

    /**
     * Дочерние элементы
     */
    children?: ReactNode;
}

const ThemeContext = React.createContext<ThemeContextProps | null>(null);

let GLOBAL_THEME: Theme | null = null;

/**
 * Устанавливает глобальную тему
 *
 * @deprecated Функция предназначена только для использования в useApplyGlobalTheme.
 * Вместо нее используйте RootThemeProvider.
 */
export const setGlobalTheme = (theme: Theme) => {
    GLOBAL_THEME = theme;
};

/**
 * Применяет глобальную тему в обход стандартного механизма с контекстом.
 * Хук является опасным решением, созданным для оптимизации производительности в Commander.
 * Стандартный подход с использованием контекста снижал производительность интерфейса Commander
 * из-за совместного использования React и legacy-кода на БЭМ.
 *
 * @deprecated Хук предназначен только для использования в Commander.
 * Вместо него используйте RootThemeProvider.
 */
export function useApplyGlobalTheme(theme: Theme) {
    setGlobalTheme(theme);

    const themeClasses = [theme.main, theme.root].filter(Boolean).join(' ');

    useIsomorphicLayoutEffect(() => {
        addRootTheme(themeClasses);
    }, [themeClasses]);
}

/**
 * Хук для получения темы с фолбеком на контекст
 * @returns объект темы
 */
export const useTheme = () => {
    const contextTheme = useContext(ThemeContext);

    if (contextTheme) {
        return contextTheme;
    }

    if (GLOBAL_THEME) {
        return GLOBAL_THEME;
    }

    throw new Error('Хук useTheme должен использоваться внутри RootThemeProvider');
};

export const InternalThemeProvider: React.FC<ThemeProviderProps> = function InternalThemeProvider({ theme, children }) {
    return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
};

export const RootThemeProvider: React.FC<ThemeProviderProps> = memo(function ThemeProvider(props) {
    const { theme, rootElement, children } = props;

    useIsomorphicLayoutEffect(() => {
        if (rootElement === null) {
            return;
        }

        addRootTheme(getClassNames([theme.main, theme.root]), rootElement);
    }, [theme.main, theme.root, rootElement]);

    return <InternalThemeProvider theme={theme}>{children}</InternalThemeProvider>;
});

/**
 * Хук для получения инвертированной темы приложения
 * @kind helper
 */
export function useInvertedTheme() {
    const theme = useTheme();

    return getClassNames([theme.inverted, theme.root]);
}
